// @ts-nocheck
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

type Status =
  | { kind: 'loading' }
  | { kind: 'checked_in'; team?: string; event?: string; time?: string }
  | { kind: 'already'; team?: string; time?: string }
  | { kind: 'error'; message: string };

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_TOKEN: "This QR code isn't valid.",
  UNAUTHORIZED_SCANNER: "You're not authorized to check in teams at this event.",
};

function fmt(ts?: string) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export default function CheckinScanPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  const runScan = useCallback(async () => {
    if (!token) {
      setStatus({ kind: 'error', message: "This QR code isn't valid." });
      return;
    }
    setStatus({ kind: 'loading' });
    const { data, error } = await invokeEdgeFunction('rle-checkin-scan', {
      body: { checkin_token: token },
    });

    if (error || !data?.ok) {
      const code =
        data?.error?.code || data?.code || (data?.error && typeof data.error === 'string' ? data.error : '');
      const raw = data?.error?.message || data?.message || error?.message;
      setStatus({
        kind: 'error',
        message: ERROR_MESSAGES[code] || ERROR_MESSAGES[raw] || raw || 'Check-in failed. Please try again.',
      });
      return;
    }

    const d = data.data ?? {};
    const team = d.team?.name ?? d.team_name;
    const event = d.event?.name ?? d.event_name;
    if (d.result === 'already_checked_in') {
      setStatus({ kind: 'already', team, time: fmt(d.checked_in_at) });
    } else {
      setStatus({ kind: 'checked_in', team, event, time: fmt(d.checked_in_at) });
    }
  }, [token]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?redirect=/checkin/${token}`, { replace: true });
      return;
    }
    runScan();
  }, [loading, user, token, navigate, runScan]);

  return (
    <>
      <Helmet>
        <title>Team Check-In | RecruitLook Events</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <main className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12 text-center font-body">
        <div className="w-full max-w-sm space-y-8">
          {status.kind === 'loading' && (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-[#06B6D4]" />
              <p className="text-lg text-muted-foreground">Checking in…</p>
            </div>
          )}

          {status.kind === 'checked_in' && (
            <div className="flex flex-col items-center gap-4">
              <CheckCircle2 className="h-20 w-20 text-green-600" />
              <h1 className="text-3xl font-bold leading-tight">✓ {status.team ?? 'Team'} checked in</h1>
              {status.event && <p className="text-xl text-muted-foreground">{status.event}</p>}
              {status.time && <p className="text-base text-muted-foreground">{status.time}</p>}
            </div>
          )}

          {status.kind === 'already' && (
            <div className="flex flex-col items-center gap-4">
              <AlertTriangle className="h-20 w-20 text-amber-500" />
              <h1 className="text-2xl font-bold leading-tight">
                {status.team ?? 'Team'} was already checked in
              </h1>
              {status.time && <p className="text-lg text-muted-foreground">at {status.time}</p>}
            </div>
          )}

          {status.kind === 'error' && (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="h-20 w-20 text-destructive" />
              <h1 className="text-2xl font-bold leading-tight">{status.message}</h1>
            </div>
          )}

          {status.kind !== 'loading' && (
            <Button
              size="lg"
              className="h-14 w-full text-lg bg-[#06B6D4] hover:bg-[#0891b2] text-white"
              onClick={runScan}
            >
              Scan another
            </Button>
          )}
        </div>
      </main>
    </>
  );
}
